import { Box, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Mui } from "@leap4/frontend";
import React, { useContext, useState } from "react";
import { ReportContext } from "../reportContext";
import { SavedTemplate } from "../templates/types";
import { extractErrorMessage } from "../../../../utils/functions";

const { Paper, Divider } = Mui;

interface Props {
  openTemplate: (template: SavedTemplate) => Promise<void>;
  deleteReport: (template: SavedTemplate) => Promise<void>;
  overwriteTemplate?: (template: SavedTemplate) => Promise<void>;
  copyTemplate?: (template: SavedTemplate) => Promise<void>;
  createTemplate: () => void;
  refresh: () => Promise<void>;
  loading: boolean;
  busy: boolean;
  error: string | null;
}

type Pending = { kind: "delete" | "overwrite"; template: SavedTemplate };

const summaryOf = (t: SavedTemplate): string => {
  const range = t.preset && t.preset !== "choose time preset" ? t.preset.replace("last calender", "Last calendar") : `${t.start_date ?? "?"} → ${t.end_date ?? "?"}`;
  return [t.campaign, t.report_type === "pixel" ? "Impression date" : "Conversion date", range].filter(Boolean).join(" · ");
};

const SavedTemplates: React.FC<Props> = (props) => {
  const { reportTemplates, report } = useContext(ReportContext);
  const [pending, setPending] = useState<Pending | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const confirm = async () => {
    if (!pending) return;
    try {
      if (pending.kind === "delete") await props.deleteReport(pending.template);
      else await props.overwriteTemplate?.(pending.template);
      setPending(null);
    } catch (e) {
      setActionError(extractErrorMessage(e) || (e instanceof Error ? e.message : `Could not ${pending.kind} the template.`));
    }
  };

  const copy = async (template: SavedTemplate) => {
    setActionError(null);
    try {
      await props.copyTemplate?.(template);
    } catch (e) {
      setActionError(extractErrorMessage(e) || "Could not copy the template.");
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Shared within your organization. Load a template to review its settings, then run it.
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={props.refresh} disabled={props.loading || props.busy}>
            Refresh
          </Button>
          <Button variant="contained" disabled={props.busy} onClick={props.createTemplate}>
            Create template
          </Button>
        </Box>
      </Box>

      {(props.error || actionError) && (
        <Typography role="alert" color="error" sx={{ mb: 2 }}>
          {actionError || props.error}
        </Typography>
      )}

      {!props.loading && !props.error && !reportTemplates.length ? (
        <Paper sx={{ border: 1, borderColor: "divider", boxShadow: "none", borderRadius: "12px", py: 6, px: 3, textAlign: "center" }}>
          <Typography variant="h6">No saved templates yet</Typography>
          <Typography color="text.secondary" sx={{ my: 1.5 }}>
            Choose dimensions, metrics and a time range in the builder, then save the configuration as a template.
          </Typography>
          <Button variant="outlined" onClick={props.createTemplate}>
            Create your first template
          </Button>
        </Paper>
      ) : (
        <Paper component="ul" aria-label="Saved templates" sx={{ border: 1, borderColor: "divider", boxShadow: "none", borderRadius: "12px", listStyle: "none", p: 0, m: 0, overflow: "hidden" }}>
          {reportTemplates.map((t, i) => {
            const isOpen = report._id === t.id;
            return (
              <Box component="li" key={t.id}>
                {i > 0 && <Divider />}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 2.5, py: 1.75, flexWrap: "wrap" }}>
                  <Box sx={{ flex: 1, minWidth: 220 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {t.name}
                      {isOpen && (
                        <Typography component="span" variant="caption" color="primary" sx={{ ml: 1 }}>
                          Loaded
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {summaryOf(t)}
                      {t.created_by ? ` · by ${t.created_by}` : ""}
                      {t.updated_at ? ` · updated ${new Date(t.updated_at).toLocaleDateString("en-GB")}` : ""}
                    </Typography>
                    {t.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                        {t.description}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    <Button size="small" variant="outlined" disabled={props.busy} onClick={() => props.openTemplate(t)}>
                      Load
                    </Button>
                    {props.overwriteTemplate && (
                      <Button size="small" disabled={props.busy} onClick={() => { setActionError(null); setPending({ kind: "overwrite", template: t }); }}>
                        Overwrite
                      </Button>
                    )}
                    {props.copyTemplate && (
                      <Button size="small" disabled={props.busy} onClick={() => copy(t)}>
                        Copy
                      </Button>
                    )}
                    <Button size="small" color="error" disabled={props.busy} onClick={() => { setActionError(null); setPending({ kind: "delete", template: t }); }}>
                      Delete
                    </Button>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Paper>
      )}

      <Dialog open={!!pending} onClose={() => { if (!props.busy) setPending(null); }} slotProps={{ paper: { role: "alertdialog" } }}>
        <DialogTitle>{pending?.kind === "delete" ? "Delete template?" : `Overwrite "${pending?.template.name}"?`}</DialogTitle>
        <DialogContent>
          <Typography>
            {pending?.kind === "delete"
              ? `"${pending?.template.name}" will be removed for everyone in this organization. Existing report data is unaffected.`
              : `"${pending?.template.name}" will be replaced with the settings currently in the report builder, for everyone in this organization.`}
          </Typography>
          {actionError && (
            <Typography role="alert" color="error" sx={{ mt: 1.5 }}>
              {actionError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={props.busy} onClick={() => setPending(null)}>
            Cancel
          </Button>
          <Button variant="contained" color={pending?.kind === "delete" ? "error" : "primary"} disabled={props.busy} onClick={confirm}>
            {props.busy ? "Working…" : pending?.kind === "delete" ? "Delete template" : "Overwrite"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default SavedTemplates;
